import { useState } from 'react';

import { QueryError } from '@/components/ui/QueryError';
import { SkeletonLines } from '@/components/ui/Skeleton';
import type { ApiRequestError } from '@/lib/apiClient';
import { formatDateTime } from '@/lib/format';
import type { BroadcastDetail } from '@/types/messaging';
import { NotificationDetailHeader } from './NotificationDetailHeader';
import { NotificationOverviewCard } from './NotificationOverviewCard';
import { NotificationDeliverySummaryTab } from './NotificationDeliverySummaryTab';
import { NotificationMessageDetailsTab } from './NotificationMessageDetailsTab';

interface NotificationDetailViewProps {
  uuid: string;
  data: BroadcastDetail | null;
  isLoading: boolean;
  error: ApiRequestError | null;
  onRetry: () => void;
  onBack: () => void;
}

/** How a send's status reads in the header. */
const STATUS_LABEL: Record<BroadcastDetail['status'], string> = {
  SENT: 'Sent',
  PARTIAL: 'Partly sent',
  FAILED: 'Failed',
};

export function NotificationDetailView({
  uuid,
  data,
  isLoading,
  error,
  onRetry,
  onBack,
}: NotificationDetailViewProps) {
  const [activeTab, setActiveTab] = useState<'summary' | 'details'>('summary');

  if (error) {
    return (
      <div className="flex flex-col gap-6 text-slate-900 dark:text-slate-100">
        <NotificationDetailHeader onBack={onBack} />
        <QueryError title="Could not load this notification" error={error} onRetry={onRetry} />
      </div>
    );
  }

  if (isLoading || !data) {
    return (
      <div className="flex flex-col gap-6 text-slate-900 dark:text-slate-100">
        <NotificationDetailHeader onBack={onBack} />
        <div className="rounded-3xl bg-white dark:bg-[#111c2e] border border-slate-200 dark:border-slate-800 p-6 shadow-xs">
          <SkeletonLines count={6} />
        </div>
      </div>
    );
  }

  const channel = data.channel === 'SMS' ? 'SMS' : 'WhatsApp';
  const sentDate = formatDateTime(data.sentAt);

  return (
    <div className="flex flex-col gap-6 text-slate-900 dark:text-slate-100">
      {/* Header */}
      <NotificationDetailHeader
        title={data.title}
        status={STATUS_LABEL[data.status]}
        sentDate={sentDate}
        channel={channel}
        audience={data.audienceLabel}
        onBack={onBack}
      />

      {/* Top Overview Card */}
      <NotificationOverviewCard
        message={data.message}
        channel={channel}
        audience={data.audienceLabel}
        recipientsCount={data.recipientsCount}
        deliveredCount={data.deliveredCount}
        failedCount={data.failedCount}
        pendingCount={data.pendingCount}
      />

      {/* Tab Switcher */}
      <div className="flex items-center gap-8 border-b border-slate-200 dark:border-slate-800 -mb-1">
        <button
          type="button"
          onClick={() => setActiveTab('summary')}
          className={`pb-3.5 text-[14.5px] font-black transition-colors cursor-pointer relative ${
            activeTab === 'summary'
              ? 'text-indigo-600 dark:text-indigo-400 border-b-2 border-indigo-600 dark:border-indigo-400 -mb-px'
              : 'text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200 font-extrabold'
          }`}
        >
          Delivery summary
        </button>

        <button
          type="button"
          onClick={() => setActiveTab('details')}
          className={`pb-3.5 text-[14.5px] font-black transition-colors cursor-pointer relative ${
            activeTab === 'details'
              ? 'text-indigo-600 dark:text-indigo-400 border-b-2 border-indigo-600 dark:border-indigo-400 -mb-px'
              : 'text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200 font-extrabold'
          }`}
        >
          Message details
        </button>
      </div>

      {/* Tab Content */}
      {activeTab === 'summary' ? (
        <NotificationDeliverySummaryTab
          broadcastUuid={uuid}
          audience={data.audienceLabel}
          totalRecipients={data.recipientsCount}
          deliveredCount={data.deliveredCount}
          failedCount={data.failedCount}
          pendingCount={data.pendingCount}
        />
      ) : (
        <NotificationMessageDetailsTab
          notificationId={data.uuid}
          type={data.templateName ?? data.title}
          channel={channel}
          audience={data.audienceLabel}
          sentOn={sentDate}
          sentBy={data.sentBy}
          recipientsCount={data.recipientsCount}
          deliveredCount={data.deliveredCount}
          failedCount={data.failedCount}
          pendingCount={data.pendingCount}
          message={data.message}
          charCount={data.characters}
          smsParts={data.smsParts}
          templateUsed={data.templateName}
          sentAt={data.sentAt}
        />
      )}
    </div>
  );
}
